import { dailyTasksFor, loadEconomy, utcDay } from '@runkr/config/economy'
import type { DailyTask, Economy } from '@runkr/config/economy'
import type { RewardTask } from '@runkr/schema'
import type { DailyRow } from './dailyApi'

/** One of today's tasks as the box shows it (D-053 `DailyView`). */
export interface DailyTaskView {
  id: string
  text: string
  progress: number
  target: number
  done: boolean
  coins: number
  xp: number
}

/** D-053's `DailyView`: today's tasks and the hours until the UTC midnight reset. */
export interface DailyView {
  tasks: DailyTaskView[]
  hoursToReset: number
}

let cached: Economy | null = null

/** `economy.json`, validated once (the client already loads it for the level curve). */
export function economy(): Economy {
  cached ??= loadEconomy()
  return cached
}

/** The pool entry for a task id, or undefined for an id the config no longer has. */
export function taskById(id: string, eco: Economy = economy()): DailyTask | undefined {
  return eco.daily.tasks.find((t) => t.id === id)
}

const HOUR_MS = 3_600_000

/** Whole hours to the next UTC midnight, rounded up (1..24). */
export function hoursToReset(now: Date): number {
  const next = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1)
  return Math.max(1, Math.ceil((next - now.getTime()) / HOUR_MS))
}

/**
 * Today's box (T2.5): the user's pick for `now`'s UTC day (`dailyTasksFor`, as the server
 * pays it) merged with the own rows of that day; a task without a row is at 0. Progress is
 * capped at the target; done when it reached it or the row says it was completed.
 */
export function dailyView(
  userId: string,
  now: Date,
  rows: readonly DailyRow[],
  eco: Economy = economy(),
): DailyView {
  const tasks: DailyTaskView[] = []
  for (const id of dailyTasksFor(userId, utcDay(now), eco)) {
    const task = taskById(id, eco)
    if (!task) continue
    const row = rows.find((r) => r.taskId === id)
    const progress = Math.max(0, Math.min(task.target, Math.floor(row?.progress ?? 0)))
    tasks.push({
      id,
      text: task.text,
      progress,
      target: task.target,
      done: progress >= task.target || row?.completed === true,
      coins: task.coins,
      xp: task.xp,
    })
  }
  return { tasks, hoursToReset: hoursToReset(now) }
}

/** The view with a race's `"reward"` tasks applied (progress only goes up within a day). */
export function withRewardTasks(view: DailyView, tasks: readonly RewardTask[]): DailyView {
  if (tasks.length === 0) return view
  return {
    ...view,
    tasks: view.tasks.map((t) => {
      const r = tasks.find((x) => x.id === t.id)
      if (!r) return t
      const progress = Math.max(t.progress, Math.min(t.target, r.progress))
      return { ...t, progress, done: t.done || r.completedNow || progress >= t.target }
    }),
  }
}
